import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function QualityDashboard({ token }) {
  const toast = useToast();
  const [alerts, setAlerts] = useState([]);
  const [alertEvents, setAlertEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '', condition_type: 'cost_spike', threshold: 0, channel: 'webhook', config: {},
  });
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = async () => {
    try {
      const [alertsRes, eventsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/protected/alerts`, { headers }),
        fetch(`${API_URL}/api/protected/alerts/events?limit=20`, { headers }),
        fetch(`${API_URL}/api/protected/traces/stats?period=24h`, { headers }),
      ]);
      if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
      if (eventsRes.ok) setAlertEvents((await eventsRes.json()).events || []);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) {}
  };

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 10000); return () => clearInterval(i); }, [token]);

  const createAlert = async () => {
    if (!form.name.trim()) { toast('Alert name is required', 'warning'); return; }
    try {
      const res = await fetch(`${API_URL}/api/protected/alerts`, {
        method: 'POST', headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to create alert'));
      toast('Alert rule created', 'success');
      setShowCreate(false);
      setForm({ name: '', condition_type: 'cost_spike', threshold: 0, channel: 'webhook', config: {} });
      fetchAll();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const toggleAlert = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/alerts/${id}/toggle`, { method: 'PATCH', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to toggle alert'));
      fetchAll();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/alerts/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete alert'));
      toast('Alert deleted', 'success');
      fetchAll();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const ackEvent = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/alerts/events/${eventId}/ack`, { method: 'PUT', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to acknowledge alert'));
      fetchAll();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const conditionLabels = {
    cost_spike: 'Cost Spike',
    latency_spike: 'Latency Spike',
    error_rate: 'Error Rate',
    anomaly: 'Anomaly',
    token_usage: 'Token Usage',
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Quality & Monitoring</div>
          <div className="page-subtitle">Anomaly detection, alerts, and health metrics</div>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>New Alert</button>
      </div>
      <div className="page-body">
        {stats && (
          <div className="metrics-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div className="metric-card blue">
              <div className="metric-label">Traces (24h)</div>
              <div className="metric-value blue">{stats.total_traces}</div>
            </div>
            <div className="metric-card red">
              <div className="metric-label">Errors</div>
              <div className="metric-value red">{stats.error_traces}</div>
            </div>
            <div className="metric-card yellow">
              <div className="metric-label">Error Rate</div>
              <div className="metric-value yellow">{(stats.error_rate * 100).toFixed(1)}%</div>
            </div>
            <div className="metric-card green">
              <div className="metric-label">Avg Latency</div>
              <div className="metric-value green">{stats.avg_duration_ms < 1000 ? `${Math.round(stats.avg_duration_ms)}ms` : `${(stats.avg_duration_ms / 1000).toFixed(1)}s`}</div>
            </div>
            <div className="metric-card purple">
              <div className="metric-label">Total Cost</div>
              <div className="metric-value purple">${stats.total_cost.toFixed(4)}</div>
            </div>
          </div>
        )}

        <div className="tabs">
          <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Alert Events ({alertEvents.filter(e => !e.acknowledged).length})</button>
          <button className={`tab-btn ${tab === 'alerts' ? 'active' : ''}`} onClick={() => setTab('alerts')}>Alert Rules ({alerts.length})</button>
        </div>

        {showCreate && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Create Alert Rule</div>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. High Error Rate Alert" />
              </div>
              <div className="form-group">
                <label>Condition</label>
                <select value={form.condition_type} onChange={e => setForm({ ...form, condition_type: e.target.value })}>
                  <option value="cost_spike">Cost Spike</option>
                  <option value="latency_spike">Latency Spike</option>
                  <option value="error_rate">Error Rate</option>
                  <option value="anomaly">Anomaly</option>
                  <option value="token_usage">Token Usage</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Threshold</label>
                <input type="number" step="0.01" value={form.threshold} onChange={e => setForm({ ...form, threshold: parseFloat(e.target.value) })} />
              </div>
              <div className="form-group">
                <label>Channel</label>
                <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                  <option value="webhook">Webhook</option>
                  <option value="email">Email</option>
                  <option value="slack">Slack</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={createAlert}>Create</button>
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        )}

        {tab === 'overview' && (
          <div className="card">
            {alertEvents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">&bull;</div>
                <h3>No alert events</h3>
                <p>Set up alert rules to get notified when quality metrics cross thresholds.</p>
              </div>
            ) : (
              <div className="timeline">
                {alertEvents.map(e => (
                  <div className="timeline-item" key={e.id}>
                    <div className={`timeline-dot ${e.acknowledged ? 'info' : 'warning'}`} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>{e.alert_name}</div>
                        <div className="text-sm">{e.message}</div>
                        <div className="text-xs text-muted" style={{ marginTop: 4 }}>{new Date(e.created_at).toLocaleString()}</div>
                      </div>
                      <div>
                        {e.acknowledged ? (
                          <span className="badge badge-neutral">Acked</span>
                        ) : (
                          <button className="btn-ghost btn-sm" onClick={() => ackEvent(e.id)}>Acknowledge</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'alerts' && (
          <div className="card">
            {alerts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">&bull;</div>
                <h3>No alert rules</h3>
                <p>Create rules to monitor for cost spikes, high error rates, latency anomalies, and more.</p>
                <button className="btn-primary" onClick={() => setShowCreate(true)}>Create Alert Rule</button>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Alert</th>
                    <th>Condition</th>
                    <th>Threshold</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Recent (24h)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{a.name}</td>
                      <td><span className="badge badge-info">{conditionLabels[a.condition_type] || a.condition_type}</span></td>
                      <td className="text-mono">{a.threshold}</td>
                      <td>{a.channel}</td>
                      <td>
                        <button className="btn-ghost btn-sm" onClick={() => toggleAlert(a.id)} style={{ color: a.enabled ? 'var(--success)' : 'var(--text-muted)' }}>
                          <span className={`status-dot ${a.enabled ? 'active' : 'inactive'}`} />
                          {a.enabled ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td>{a.recent_events > 0 ? <span className="badge badge-warning">{a.recent_events}</span> : '0'}</td>
                      <td><button className="btn-danger btn-sm" onClick={() => deleteAlert(a.id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default QualityDashboard;
